/**
 * Attendant Mobile view — /housekeeping/me
 * Thumb-friendly, responsive. No PWA, no offline mode (ADR D10).
 */
import { useCallback, useEffect, useState } from "react";
import { PlayCircle, PauseCircle, CheckCheck, ThumbsUp } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import RoomReadinessIndicator from "@/components/room/RoomReadinessIndicator";

const STATE_LABEL = {
  pending: "Pending", assigned: "Assigned", accepted: "Accepted",
  in_progress: "Cleaning", paused: "Paused", pending_inspection: "Waiting Inspection",
};

export default function MyTasks() {
  const [tasks, setTasks] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/housekeeping/my-tasks"); setTasks(data); }
    catch (e) { toast.error(formatApiError(e) || "Failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action, extra) => {
    try {
      await api.post(`/housekeeping/tasks/${id}/${action}`, extra || {});
      toast.success(action); await load();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  return (
    <div className="space-y-4 max-w-2xl mx-auto pb-24" data-testid="my-tasks-page">
      <PageHeader title="My Rooms" subtitle="Your assignments for today."/>
      {tasks.length === 0 && (
        <div className="hocp-card p-6 text-center text-hocp-textTertiary" data-testid="my-tasks-empty">
          No tasks assigned. Enjoy the break.
        </div>
      )}
      {tasks.map(t => (
        <div key={t.id} className="hocp-card p-5" data-testid={`my-task-${t.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-display font-bold text-2xl">Room {t.room_number}</div>
              <div className="text-[13px] text-hocp-textSecondary capitalize">{t.task_type.replace(/_/g, " ")}</div>
              <div className="flex gap-2 mt-2 flex-wrap">
                <Badge tone="info" size="sm">{STATE_LABEL[t.state]}</Badge>
                <Badge tone="warning" size="sm">{t.priority_code}</Badge>
                {t.require_inspection && <Badge tone="warning" size="sm">Inspection required</Badge>}
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            {t.state === "assigned" && (
              <Button size="lg" className="flex-1" onClick={() => act(t.id, "accept")} data-testid={`my-accept-${t.id}`}>
                <ThumbsUp className="w-4 h-4 mr-2"/> Accept
              </Button>
            )}
            {["accepted", "paused"].includes(t.state) && (
              <Button size="lg" className="flex-1" onClick={() => act(t.id, "start")} data-testid={`my-start-${t.id}`}>
                <PlayCircle className="w-4 h-4 mr-2"/> Start
              </Button>
            )}
            {t.state === "in_progress" && (
              <>
                <Button size="lg" variant="outline"
                        onClick={() => act(t.id, "pause", { reason: "break" })}
                        data-testid={`my-pause-${t.id}`}>
                  <PauseCircle className="w-4 h-4 mr-2"/> Pause
                </Button>
                <Button size="lg" className="flex-1" onClick={() => act(t.id, "complete", {})}
                        data-testid={`my-complete-${t.id}`}>
                  <CheckCheck className="w-4 h-4 mr-2"/> Complete
                </Button>
              </>
            )}
            {t.state === "pending_inspection" && (
              <div className="flex-1 text-center text-[13px] text-hocp-textSecondary italic p-2">
                Waiting for supervisor inspection…
              </div>
            )}
          </div>
          {t.checklist_snapshot && t.state === "in_progress" && (
            <details className="mt-3">
              <summary className="cursor-pointer text-[12px] text-hocp-textSecondary">Checklist ({t.checklist_snapshot.length} items)</summary>
              <ul className="mt-2 space-y-1 text-[13px]">
                {t.checklist_snapshot.map(it => (
                  <li key={it.item_code}>• {it.label}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}
